/**
 * Owns the SDS draft hook + panel so Inventory panel stays under the
 * file-size hygiene cap (MSDS-3–5).
 */
import { useCallback, useRef } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ManufacturingMaterialSdsPanel } from "./ManufacturingMaterialSdsPanel";
import { useManufacturingMaterialSds } from "./useManufacturingMaterialSds";

type Props = {
  open: boolean;
  enabled: boolean;
  material: MfgMaterial | null;
  onClose: () => void;
};

export function ManufacturingMaterialSdsHost({ open, enabled, material, onClose }: Props) {
  const sds = useManufacturingMaterialSds({ open, enabled, material });
  const sdsRef = useRef(sds);
  sdsRef.current = sds;

  const handleClose = useCallback(() => {
    sdsRef.current.reset();
    onClose();
  }, [onClose]);

  if (!open || !material) return null;

  const active = sds.sdsDocument ?? sds.publishedDocument;

  return (
    <ManufacturingMaterialSdsPanel
      open={open}
      material={material}
      loading={sds.loading}
      saving={sds.saving}
      generating={sds.generating}
      exporting={sds.exporting}
      acknowledging={sds.acknowledging}
      publishing={sds.publishing}
      uploading={sds.uploading}
      error={sds.error}
      dirty={sds.dirty}
      ackOpen={sds.ackOpen}
      signOffChecked={sds.signOffChecked}
      sdsDocument={sds.sdsDocument}
      publishedDocument={sds.publishedDocument}
      sections={sds.sections}
      sectionAudit={active?.section_audit ?? {}}
      onSetSection={sds.setSection}
      onSave={() => void sdsRef.current.save()}
      onGenerate={(hints) => void sdsRef.current.generate(hints)}
      onAcknowledge={() => void sdsRef.current.acknowledgeOpen()}
      onPublish={() => void sdsRef.current.publish()}
      onUploadSupplier={(file) => void sdsRef.current.uploadSupplier(file)}
      onSignOffChange={sds.setSignOffChecked}
      onExportPdf={() => void sdsRef.current.exportPdf()}
      onClose={handleClose}
    />
  );
}
